// Hooks for the Agenda tab.
import { useState, useRef, useEffect } from "react";
import { AGENDA_STATE, LOAD_ERROR_MESSAGE } from "./agenda.constants";
import { POLL_INTERVAL_MS } from "../insights.constants";
import { useInsightPolling, useAddedHighlight } from "../insights.hooks";
import { scrollToBottom } from "../insights.utils";
import {
  addAgendaItem,
  updateAgendaItem,
  deleteAgendaItem,
  fromBackend,
  makeAgendaItem,
  mostRecentlyCompletedId,
} from "./agenda.utils";
import LogManager from "../../../utils/logManager";

/**
 * useAgendaItems - Agenda-specific configuration of the shared useInsightPolling hook.
 *
 * Fetches from /agenda on mount and falls back to polling only while the WebSocket is
 * disconnected. `requestExtras` adds the endpoint's required `operation`, and `mapItems`
 * normalizes the backend shape (itemId/content/status) to the frontend one (id/title/state)
 * before rows reach the store.
 *
 * Deliberately does NOT set `useSince` (Action Items does): agenda is not append-only — existing
 * rows change status as the meeting progresses, so each poll needs the full list.
 *
 * Order and deletions are both reconciled now: the store re-sorts with compareAgendaItems on
 * every write, and `pruneMissing` drops rows a full snapshot no longer contains.
 */
export const useAgendaItems = (meetingUuid, insightStore) =>
  useInsightPolling({
    meetingUuid,
    insightStore,
    endpoint: "/agenda",
    pollIntervalMs: POLL_INTERVAL_MS,
    storeKey: "agendaItems",
    responseKey: "agendaItems",
    upsertKey: "upsertAgendaItems",
    errorLabel: "Failed to fetch agenda items:",
    pollErrorLabel: "Fallback poll for agenda items failed:",
    errorMessage: LOAD_ERROR_MESSAGE,
    requestExtras: { operation: "query" },
    mapItems: fromBackend,
    // The response is a full snapshot, so absent rows mean deleted — prune them.
    upsertOptions: { pruneMissing: true },
  });

/**
 * useRecapExpansion - open/closed state for per-item recap panels.
 *
 * The most recently completed item's recap opens on its own; everything else is user-driven, and
 * several can be open at once. `autoOpenedRef` records every id already auto-opened, so a recap the
 * user collapsed stays closed — including when an earlier item becomes the newest again, which
 * happens if the conversation circles back and that item completes a second time.
 *
 * State is per-mount. SessionView renders the Agenda tab conditionally, so switching tabs away and
 * back collapses everything and re-opens the newest recap.
 */
export const useRecapExpansion = (items) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const autoOpenedRef = useRef(new Set());

  const newestCompletedId = mostRecentlyCompletedId(items);
  useEffect(() => {
    if (newestCompletedId && !autoOpenedRef.current.has(newestCompletedId)) {
      autoOpenedRef.current.add(newestCompletedId);
      setExpandedIds((prev) => new Set(prev).add(newestCompletedId));
    }
  }, [newestCompletedId]);

  const toggle = (item) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(item.id)) {
        next.add(item.id);
      }
      return next;
    });

  return {
    // Every completed item expands, whether or not generation produced text
    isExpandable: (item) => item.state === AGENDA_STATE.COMPLETED,
    isExpanded: (item) => expandedIds.has(item.id),
    toggle,
  };
};

/**
 * useAgendaActions - Add/edit/delete UI state and handlers, with optimistic writes through
 * insightStore. Add/edit input values live here since each row is a single title field.
 * Edit and add rows stay open until their own save/cancel, so several can be open at once; the
 * delete confirmation is single-row and any other action cancels it.
 */
export const useAgendaActions = ({ meetingUuid, insightStore, scrollRef }) => {
  const [editingIds, setEditingIds] = useState(() => new Set());
  const [editValues, setEditValues] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const prevTitles = useRef({});
  const { highlightId, flash } = useAddedHighlight();

  const setEditValue = (itemId, value) => setEditValues((prev) => ({ ...prev, [itemId]: value }));

  // Drops every piece of a row's edit state; shared by save and cancel.
  const closeEdit = (itemId) => {
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
    setEditValues((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    delete prevTitles.current[itemId];
  };

  const startEdit = (item) => {
    setPendingDeleteId(null); // another action cancels an open delete confirmation
    setEditingIds((prev) => new Set(prev).add(item.id));
    setEditValues((prev) => ({ ...prev, [item.id]: item.title }));
    prevTitles.current[item.id] = item.title;
  };

  const saveEdit = (itemId) => {
    const title = (editValues[itemId] || "").trim();
    const prevTitle = prevTitles.current[itemId];
    setPendingDeleteId(null);

    if (title) {
      insightStore.upsertAgendaItems([{ id: itemId, title }], { force: true });

      updateAgendaItem(meetingUuid, itemId, title)
        .then((updated) => {
          if (!updated) {
            insightStore.upsertAgendaItems([{ id: itemId, title: prevTitle }], { force: true });
          }
        })
        .catch((err) => {
          LogManager.error("Failed to update agenda item, rolling back", err);
          insightStore.upsertAgendaItems([{ id: itemId, title: prevTitle }], { force: true });
        });
    }
    closeEdit(itemId);
  };

  const cancelEdit = (itemId) => {
    setPendingDeleteId(null);
    closeEdit(itemId);
  };

  const startAdd = () => {
    setPendingDeleteId(null);
    setIsAdding(true);
  };

  const saveAdd = () => {
    const title = addValue.trim();
    if (title) {
      setPendingDeleteId(null);
      const id = `agenda#${crypto.randomUUID()}`;
      insightStore.upsertAgendaItems([makeAgendaItem({ id, title })]);
      flash(id);
      scrollToBottom(scrollRef);

      addAgendaItem(meetingUuid, id, title)
        .then((created) => {
          if (!created) {
            LogManager.warn("Add returned CONFLICT — rolling back");
            insightStore.removeAgendaItem(id);
            return;
          }
          // Apply the server's row: it carries the detectedAt that makes this item versionable,
          // and only a versioned item passes the agenda_status_changed gate.
          insightStore.upsertAgendaItems([created]);
        })
        .catch((err) => {
          LogManager.error("Failed to add agenda item, rolling back", err);
          insightStore.removeAgendaItem(id);
        });
    }
    setAddValue("");
    setIsAdding(false);
  };

  const cancelAdd = () => {
    setPendingDeleteId(null);
    setAddValue("");
    setIsAdding(false);
  };

  // Delete confirmation — transient and single-row. Starting it on another row (or the delete
  // itself) replaces/clears the current one; opening an edit/add also clears it (see above).
  const startDelete = (item) => setPendingDeleteId(item.id);
  const confirmDelete = (item) => {
    insightStore.removeAgendaItem(item.id);
    setPendingDeleteId(null);

    deleteAgendaItem(meetingUuid, item.id).catch((err) => {
      LogManager.error("Failed to delete agenda item, rolling back", err);
      // Append to end — recomputing original position is not reliable after concurrent
      // mutations, and append is safe (the next poll will reorder if needed).
      insightStore.upsertAgendaItems([item], { force: true });
    });
  };
  const cancelDelete = () => setPendingDeleteId(null);

  return {
    add: { open: isAdding, start: startAdd, save: saveAdd, cancel: cancelAdd, value: addValue, setValue: setAddValue },
    edit: { ids: editingIds, start: startEdit, save: saveEdit, cancel: cancelEdit, values: editValues, setValue: setEditValue },
    del: { pendingId: pendingDeleteId, start: startDelete, confirm: confirmDelete, cancel: cancelDelete },
    highlightId,
  };
};
